import { useMemo, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { ReactComponent as VlrTokenIcon } from '../assets/svg/token_image.svg';
import { ReactComponent as AwardBadgeDark } from '../assets/svg/award_badge-dark.svg';
import { ReactComponent as AwardBadgeLight } from '../assets/svg/award_badge-light.svg';
import CampaignRow from './CampaignRow';
import SablierCampaignRow from './SablierCampaignRow';
import StakeBanner from './StakeBanner';
import NewsletterBlock from './NewsletterBlock';
import HelpCenterBlock from './HelpCenterBlock';
import ClaimVlrModal from './ClaimVlrModal';
import claimBannerTextureDark from '../assets/png/claim-banner-texture_dark.png';
import {
  getOngoingCampaigns,
  getPreviousCampaigns,
  getSablierCampaigns,
} from '../utils/campaignData';
import { useTotalWeiClaimable } from '../claimer/useTotalWeiClaimable';
import { Skeleton } from '@mui/material';
import { useVlrTokenBalance } from '../hooks/useVlrTokenBalance';
import { useAccount } from 'wagmi';
import { DISTRIBUTED_CAMPAIGN_CODES, getUserEarnedByCampaign } from '../claimer/utils';
import { formatRewardsDisplay } from '../utils/rewardsCalculator';
import { Button, DisabledButton } from './Button';
import { CampaignCode } from '../claimer/types';

const previousCampaigns = getPreviousCampaigns();
const sablierCampaigns = getSablierCampaigns();

const ConnectedWalletContent: React.FC = () => {
  const { address } = useAccount();
  const { theme } = useTheme();
  const [isClaimModalOpen, setIsClaimModalOpen] = useState<boolean>(false);

  const ongoingCampaigns = getOngoingCampaigns();

  const prevCampaignsWithRewards = useMemo(() => {
    if (!address) return previousCampaigns;
    const rewardsByCampaign = getUserEarnedByCampaign(address);

    // debugger;
    return previousCampaigns.map((campaign) => {
      const rewardRaw = rewardsByCampaign[campaign.code];

      const rewards =
        rewardRaw >= 0
          ? formatRewardsDisplay(Number(rewardRaw) / 1e18)
          : DISTRIBUTED_CAMPAIGN_CODES.has(campaign.code as CampaignCode)
            ? '0 VLR'
            : undefined;
      return {
        ...campaign,
        rewards: rewards,
      };
    });
  }, [address]);

  const { formatted: formattedClaimable, totalWeiClaimable } = useTotalWeiClaimable();
  const { formatted: formattedBalance, balance } = useVlrTokenBalance();

  const { chainId } = useAccount();
  const isClaimButtonDisabled = chainId !== 8453 || !totalWeiClaimable;

  return (
    <div className="max-w-[1264px] mx-auto px-4 md:px-8">
      {/* Congratulations Section */}
      <div className="bg-white dark:bg-[rgba(21,23,36,0.7)] min-h-[500px] md:min-h-0 rounded-xl border border-contrast-7 dark:border-contrast-3 relative mb-8 md:mb-[60px] overflow-hidden">
        {/* Background Texture - positioned in top right */}
        <div
          className="absolute top-0 right-0 w-[612px] h-[342px] bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url(${claimBannerTextureDark})`,
            backgroundPosition: 'top right',
            backgroundSize: '612px 342px',
          }}
        />

        {/* Award Badge - positioned absolutely */}
        <div className="absolute w-[74px] h-[74px] left-[32px] top-[-11px]">
          {theme === 'dark' ? (
            <AwardBadgeDark className="w-full h-full" />
          ) : (
            <AwardBadgeLight className="w-full h-full" />
          )}
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-[72px] px-4 pb-4 md:pt-8 md:px-8 md:pb-8 h-full relative">
          {/* Left Side - Congratulations Text */}
          <div className="flex flex-col gap-4 md:gap-4 w-full md:max-w-[460px] mb-6 md:mb-0">
            <h2 className="text-contrast-3 dark:text-white text-[20px] md:text-[24px] font-medium font-geist">
              Congratulations!
            </h2>
            <h3 className="text-primary-papaya text-[16px] md:text-[18px] font-normal font-geist leading-[21px]">
              You are eligible for the Velora Token Claim!
            </h3>
            <div className="text-contrast-5 dark:text-contrast-6 text-[14px] font-normal font-geist leading-[21px] space-y-2">
              <p>
                Please make sure that your wallet is connected on{' '}
                <span className="text-contrast-3 dark:text-white font-medium font-geist">
                  Base Network
                </span>
                , then proceed to claim and stake your VLR tokens.
              </p>
              <p>
                To make it seamless for you to claim VLR, Velora Foundation distributed your VLR
                rewards on Base Network.
              </p>
            </div>
          </div>

          {/* Right Side - Action Steps */}
          <div className="flex flex-col gap-6 w-full md:w-[576px]">
            {/* Step 1 - Claim VLR */}
            <div className="bg-white dark:bg-contrast-3 border border-primary-azul rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex flex-col gap-3">
                <span className="text-primary-papaya text-[12px] font-medium font-geist">
                  STEP 1
                </span>
                <h4 className="text-contrast-5 dark:text-contrast-7 text-[16px] font-medium font-geist">
                  Claim $VLR tokens
                </h4>
                <div className="flex items-center gap-3">
                  <VlrTokenIcon className="w-6 h-6" />
                  <span className="text-contrast-3 dark:text-white text-[22px] font-medium font-geist-mono">
                    {formattedClaimable ?? <Skeleton width={80} />}
                  </span>
                </div>
              </div>
              {isClaimButtonDisabled ? (
                <DisabledButton> Claim $VLR</DisabledButton>
              ) : (
                <Button onClick={() => setIsClaimModalOpen(true)}>Claim $VLR</Button>
              )}
            </div>

            {/* Step 2 - Stake VLR */}
            <div className="bg-white dark:bg-contrast-3 border border-contrast-7 dark:border-contrast-4 rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex flex-col gap-3">
                <span className="text-primary-papaya text-[12px] font-medium font-geist">
                  STEP 2
                </span>
                <h4 className="text-contrast-5 dark:text-contrast-7 text-[16px] font-medium font-geist">
                  Stake $VLR to earn more rewards
                </h4>
                <div className="flex items-center gap-3">
                  <VlrTokenIcon className="w-6 h-6" />
                  <span className="text-contrast-3 dark:text-white text-[22px] font-medium font-geist-mono">
                    {formattedBalance ?? <Skeleton width={80} />}
                  </span>
                </div>
              </div>
              {balance && balance > 0n && formattedBalance ? (
                <Button
                  onClick={() =>
                    window.open('https://app.velora.xyz/#/earn/v3?network=base', '_blank')
                  }
                >
                  Stake $VLR
                </Button>
              ) : (
                <DisabledButton>Stake $VLR</DisabledButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns and Stake Section */}
      <div className="flex flex-col md:flex-row gap-8">
        {/* Campaigns Column */}
        <div className="flex-1">
          {/* Ongoing Campaigns */}
          <div className="mb-12">
            <h2 className="text-contrast-3 dark:text-contrast-8 text-[20px] md:text-[24px] font-medium font-geist mb-6">
              Ongoing Campaigns
            </h2>
            <div className="space-y-4">
              {/* Sablier campaigns */}
              {sablierCampaigns.map((campaign, index) => (
                <SablierCampaignRow key={`sablier-${index}`} campaign={campaign} />
              ))}
              {/* Regular ongoing campaigns */}
              {ongoingCampaigns.map((campaign, index) => (
                <CampaignRow key={`regular-${index}`} campaign={campaign} type="ongoing" />
              ))}
            </div>
          </div>

          {/* Previous Campaigns */}
          <div>
            <h2 className="text-contrast-3 dark:text-contrast-8 text-[20px] md:text-[24px] font-medium font-geist mb-6">
              Previous Campaigns
            </h2>
            <div className="space-y-4">
              {prevCampaignsWithRewards.map((campaign, index) => (
                <CampaignRow key={index} campaign={campaign} type="previous" />
              ))}
            </div>
          </div>
        </div>

        {/* Stake Banner */}
        <StakeBanner />
      </div>

      {/* Newsletter and Help Center Section */}
      <div className="flex flex-col md:flex-row gap-6 md:gap-[30px] mt-8 md:mt-[60px]">
        <NewsletterBlock />
        <HelpCenterBlock />
      </div>

      {/* Claim VLR Modal */}
      <ClaimVlrModal isOpen={isClaimModalOpen} onClose={() => setIsClaimModalOpen(false)} />
    </div>
  );
};

export default ConnectedWalletContent;
